/**
 * One-shot cleanup of the legacy `/service-worker.js` left over from the
 * static-HTML era of iSQROLL. Users who first visited the site before the
 * React migration can still have that SW registered as the controller of
 * their browser; it intercepts navigations with a stale cache and can serve
 * an /index.html that references Vite bundle hashes that no longer exist,
 * leaving the React app unable to mount and the user on an unauthenticated
 * landing page (the "random logout" symptom).
 *
 * Leaves the Vite PWA SW (`/sw.js`) and the Firebase Messaging SW
 * (`/firebase-messaging-sw.js`) alone.
 */
export async function cleanupLegacyServiceWorkers(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    for (const reg of registrations) {
      const scriptUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || ''
      if (scriptUrl.endsWith('/service-worker.js')) {
        await reg.unregister()
      }
    }

    // Drop the legacy SW's named cache so the next page load fetches fresh.
    if ('caches' in self) {
      const cacheNames = await caches.keys()
      for (const name of cacheNames) {
        if (name.startsWith('isqroll-v')) {
          await caches.delete(name)
        }
      }
    }
  } catch {
    // Best-effort — never let cleanup failures break the app.
  }
}
